/* Keyboard access for the existing desktop dropdowns; native links stay links. */
(() => {
  const desktop = window.matchMedia('(min-width: 1161px)');
  let returningFocus = false;
  let drawerObserver;
  let mobileSubmenuId = 0;
  const activeDrawerTriggers = new WeakMap();
  const mobileMenuObservers = new WeakMap();
  const featureAccordionSelector = '#shopify-section-header-inline .t4s-type__drop:has(.atease-nav-feature) .t4s-rendered-menu > .t4s-menu-item.has--children';
  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const roots = () => document.querySelectorAll('#t4s-nav-ul > .has--children');
  const submenu = item => item.querySelector(':scope > .t4s-sub-menu');
  const trigger = item => item.querySelector(':scope > a');
  function positionDropdown(item) {
    const floating = window.FloatingUIT4sDOM;
    const link = trigger(item);
    const menu = submenu(item);
    if (!desktop.matches || !floating || !link || !menu || !item.classList.contains('t4s-type__drop')) return;
    // Use Gecko's existing placement and viewport collision rules after reflow.
    floating.computePosition(link, menu, {
      placement: item.getAttribute('data-placement') || 'bottom',
      middleware: [floating.flip({}), floating.shift({ padding: 5 })]
    }).then(({ x, y, placement }) => {
      if (!desktop.matches || !menu.isConnected) return;
      menu.style.left = `${x}px`;
      menu.style.top = placement.startsWith('bottom') ? '100%' : `${y}px`;
    }).catch(() => { /* Keep the theme's last position if its engine is unavailable. */ });
  }
  let resizeTimer;
  function positionDropdowns() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => roots().forEach(positionDropdown), 0);
  }
  window.addEventListener('resize', positionDropdowns);
  window.addEventListener('load', positionDropdowns);
  function sync(item) {
    const link = trigger(item);
    const menu = submenu(item);
    if (!link || !menu) return;
    if (menu.id) link.setAttribute('aria-controls', menu.id);
    link.setAttribute('aria-expanded', String(desktop.matches &&
      (item.hasAttribute('data-atease-keyboard-open') || item.classList.contains('is-action__hover'))));
  }
  function close(item) {
    item.removeAttribute('data-atease-keyboard-open');
    sync(item);
  }
  function rootOf(target) {
    return target instanceof Element ? target.closest('#t4s-nav-ul > .has--children') : null;
  }
  function drawerTriggers(drawer) {
    return document.querySelectorAll(`[data-drawer-options][aria-controls="${drawer.id}"]`);
  }
  function isVisible(item) {
    const style = getComputedStyle(item);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' &&
      Boolean(item.offsetWidth || item.offsetHeight || item.getClientRects().length);
  }
  function drawerFocusables(drawer) {
    const items = Array.from(drawer.querySelectorAll(focusableSelector));
    if (drawer.id === 't4s-menu-drawer') {
      const close = document.querySelector('.t4s-drawer-menu__close');
      if (close) items.unshift(close);
    }
    return items.filter(isVisible);
  }
  function focusDrawer(drawer, attempt = 0) {
    requestAnimationFrame(() => {
      if (drawer.getAttribute('aria-hidden') !== 'false') return;
      const items = drawerFocusables(drawer);
      if (!items.length) {
        if (attempt < 5) setTimeout(() => focusDrawer(drawer, attempt + 1), 100);
        return;
      }
      if (drawer.contains(document.activeElement) || items.includes(document.activeElement)) return;
      items[0].focus({ preventScroll: true });
    });
  }
  function syncDrawer(drawer) {
    if (!drawer?.id) return;
    const open = drawer.getAttribute('aria-hidden') === 'false';
    const activeTrigger = activeDrawerTriggers.get(drawer);
    drawerTriggers(drawer).forEach(item => {
      item.setAttribute('aria-expanded', String(open && item === activeTrigger));
    });
    if (open) {
      focusDrawer(drawer);
      return;
    }
    if (!activeTrigger) return;
    const focused = document.activeElement;
    const focusedInDrawer = drawer.contains(focused) ||
      (drawer.id === 't4s-menu-drawer' && focused?.matches?.('.t4s-drawer-menu__close'));
    if (focused === document.body || focused === document.documentElement || focusedInDrawer) {
      activeTrigger.focus({ preventScroll: true });
    }
    activeDrawerTriggers.delete(drawer);
  }
  function initDrawers() {
    document.querySelectorAll('[data-drawer-options][aria-controls]').forEach(item => {
      const drawer = document.getElementById(item.getAttribute('aria-controls'));
      if (drawer) item.setAttribute('aria-expanded', 'false');
    });
    if (drawerObserver || !document.body) return;
    drawerObserver = new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'attributes') {
          syncDrawer(record.target);
          return;
        }
        if (record.type === 'childList') {
          initMobileMenus();
          initFeatureAccordion();
        }
        const drawer = record.target instanceof Element && record.target.closest('.t4s-drawer[aria-hidden="false"]');
        if (drawer) focusDrawer(drawer);
      });
    });
    drawerObserver.observe(document.body, { attributes: true, attributeFilter: ['aria-hidden'], childList: true, subtree: true });
  }
  function syncMobileParent(item) {
    const link = item.querySelector(':scope > a');
    const menu = item.querySelector(':scope > ul');
    if (!link || !menu) return;
    if (!menu.id) menu.id = `atease-mobile-submenu-${++mobileSubmenuId}`;
    link.setAttribute('aria-haspopup', 'true');
    link.setAttribute('aria-controls', menu.id);
    link.setAttribute('aria-expanded', String(item.classList.contains('is--opend')));
  }
  function initMobileMenus() {
    document.querySelectorAll('#t4s-menu-drawer .t4s-mb__menu').forEach(menu => {
      menu.querySelectorAll('.t4s-menu-item-has-children').forEach(syncMobileParent);
      if (mobileMenuObservers.has(menu)) return;
      const observer = new MutationObserver(records => {
        records.forEach(record => {
          if (record.type === 'childList') {
            menu.querySelectorAll('.t4s-menu-item-has-children').forEach(syncMobileParent);
            return;
          }
          const item = record.target.closest?.('.t4s-menu-item-has-children');
          if (item) syncMobileParent(item);
        });
      });
      observer.observe(menu, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
      mobileMenuObservers.set(menu, observer);
    });
  }
  function syncFeatureAccordion(item, open) {
    const link = item.querySelector(':scope > a');
    const menu = item.querySelector(':scope > .t4s-sub-menu-2');
    if (!link || !menu) return;
    if (!menu.id) menu.id = `atease-feature-submenu-${++mobileSubmenuId}`;
    item.classList.toggle('is-atease-accordion-open', open);
    link.setAttribute('aria-haspopup', 'true');
    link.setAttribute('aria-controls', menu.id);
    link.setAttribute('aria-expanded', String(open));
  }
  function initFeatureAccordion() {
    // The reference mega menu keeps every category column open at once.
    // Leaving this initializer inert also prevents hover/focus changes from
    // changing the panel height while the user moves through the links.
  }
  document.addEventListener('click', event => {
    const item = event.target.closest?.('[data-drawer-options][aria-controls]');
    if (!item) return;
    const drawer = document.getElementById(item.getAttribute('aria-controls'));
    if (drawer) activeDrawerTriggers.set(drawer, item);
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const drawer = document.querySelector('.t4s-drawer[aria-hidden="false"]');
    if (!drawer) return;
    const items = drawerFocusables(drawer);
    if (!items.length) return;
    const index = items.indexOf(document.activeElement);
    if (event.shiftKey && index <= 0) {
      event.preventDefault();
      items[items.length - 1].focus({ preventScroll: true });
    } else if (!event.shiftKey && (index === -1 || index === items.length - 1)) {
      event.preventDefault();
      items[0].focus({ preventScroll: true });
    }
  });
  document.addEventListener('focusin', event => {
    if (!desktop.matches || returningFocus) return;
    const root = rootOf(event.target);
    roots().forEach(item => { if (item !== root) close(item); });
    if (root && submenu(root)) {
      root.setAttribute('data-atease-keyboard-open', '');
      sync(root);
      positionDropdown(root);
    }
  });
  document.addEventListener('focusout', event => {
    const root = rootOf(event.target);
    if (root && !root.contains(event.relatedTarget)) close(root);
  });
  document.addEventListener('keydown', event => {
    if (!desktop.matches) return;
    const root = rootOf(event.target);
    if (!root || !submenu(root)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      root.classList.remove('is-action__hover');
      close(root);
      returningFocus = true;
      trigger(root).focus();
      returningFocus = false;
    } else if (event.key === 'ArrowDown') {
      const item = event.target.closest('.has--children');
      const menu = item && submenu(item);
      const first = menu && menu.querySelector('a[href]');
      if (first && trigger(item) === event.target) {
        event.preventDefault();
        root.setAttribute('data-atease-keyboard-open', '');
        sync(root);
        requestAnimationFrame(() => {
          if (!desktop.matches || !root.hasAttribute('data-atease-keyboard-open') || !item.contains(first)) return;
          first.focus({ preventScroll: true });
        });
      }
    }
  });
  function init() {
    roots().forEach(sync);
    initDrawers();
    initMobileMenus();
    initFeatureAccordion();
    positionDropdowns();
    document.querySelectorAll('#t4s-nav-ul').forEach(nav => {
      // Gecko changes its hover class independently of keyboard focus.
      new MutationObserver(records => {
        records.forEach(record => {
          if (record.target.parentElement === nav) sync(record.target);
        });
      }).observe(nav, { attributes: true, attributeFilter: ['class'], subtree: true });
    });
  }
  desktop.addEventListener('change', () => roots().forEach(close));
  document.addEventListener('shopify:section:load', init);
  init();
})();
